// @d4-preview-wiring (16 May 2026)
// ============================================================
// MONTHLY CLEARINGS RUN PAGE (D4 bulk preview + generate)
// ----------------------------------------------------------------
// Admin picks year/month. Page fetches /admin/billing/clearings/preview
// which returns computed clearing math for ALL active doctors PLUS
// whether each doctor already has a clearing for that period.
//
// Three things admin can do:
//   1. Just look. The table shows the projected numbers in detail.
//   2. Bulk-generate the missing ones. One call per doctor, sequential.
//   3. Force-overwrite already-existing clearings (opt-in checkbox).
//
// Stage 3 (future) will add a progress bar + per-row generate state.
// For now this page does the bulk generate sequentially with a single
// progress count.
// ============================================================
import React, { useState, useEffect, useCallback } from 'react';
import toast from 'react-hot-toast';
import { Card } from '../components/Shared';
import { billingAPI } from '../services/api';

const MONTHS_EL = [
  'Ιανουάριος','Φεβρουάριος','Μάρτιος','Απρίλιος',
  'Μάιος','Ιούνιος','Ιούλιος','Αύγουστος',
  'Σεπτέμβριος','Οκτώβριος','Νοέμβριος','Δεκέμβριος',
];

const fmtEuro = (cents) => {
  if (cents == null) return '—';
  return new Intl.NumberFormat('el-GR', {
    style: 'currency', currency: 'EUR', minimumFractionDigits: 2,
  }).format(Number(cents) / 100);
};

const ExistingBadge = ({ status }) => {
  const config = {
    pending:  { label: 'ήδη δημιουργήθηκε · σε αναμονή',  bg: '#FEF3C7', fg: '#92400E' },
    paid:     { label: 'ήδη δημιουργήθηκε · πληρώθηκε',   bg: '#D1FAE5', fg: '#065F46' },
    failed:   { label: 'ήδη δημιουργήθηκε · απέτυχε',     bg: '#FEE2E2', fg: '#991B1B' },
    on_hold:  { label: 'ήδη δημιουργήθηκε · σε αναστολή', bg: '#E0E7FF', fg: '#3730A3' },
  };
  const c = config[status] || { label: 'ήδη δημιουργήθηκε', bg: '#F3F4F6', fg: '#374151' };
  return (
    <span style={{
      padding: '4px 10px', borderRadius: 12,
      fontSize: 11, fontWeight: 600,
      background: c.bg, color: c.fg,
      whiteSpace: 'nowrap',
    }}>{c.label}</span>
  );
};

export default function MonthlyClearingsRunPage() {
  const now = new Date();
  const prevMonth = new Date(now.getFullYear(), now.getMonth() - 1, 1);

  const [year, setYear] = useState(prevMonth.getFullYear());
  const [month, setMonth] = useState(prevMonth.getMonth() + 1);
  const [overwrite, setOverwrite] = useState(false);

  const [preview, setPreview] = useState(null);
  const [loading, setLoading] = useState(false);
  const [bulkProgress, setBulkProgress] = useState({ running: false, done: 0, total: 0, errors: [] });
  // @d4-stage3-per-row-state (16 May 2026)
  // Map of doctorId → 'idle' | 'running' | 'success' | 'error' shown in real time during bulk-generate.
  const [rowStates, setRowStates] = useState({});

  const loadPreview = useCallback(async () => {
    setLoading(true);
    try {
      const res = await billingAPI.previewMonth(year, month);
      setPreview(res.data?.data || null);
    } catch (err) {
      toast.error('Αποτυχία φόρτωσης προεπισκόπησης');
      setPreview(null);
    } finally {
      setLoading(false);
    }
  }, [year, month]);

  useEffect(() => { loadPreview(); }, [loadPreview]);

  const rows = preview?.rows || [];
  const existingCount = preview?.existingCount || 0;
  const totalDoctors = preview?.doctorCount || 0;
  const missingCount = totalDoctors - existingCount;

  // Doctors who will be generated based on overwrite toggle.
  const toGenerate = rows.filter(r =>
    r.summary && !r.error && (overwrite || !r.existing)
  );

  // Totals across rows that WILL be generated.
  const totals = toGenerate.reduce((acc, r) => {
    const s = r.summary;
    acc.billable += s.billableCount || 0;
    acc.revenue += s.totalRevenueCents || 0;
    acc.doctorShare += s.doctorShareCents || 0;
    acc.commission += s.docnowCommissionCents || 0;
    acc.vat += s.docnowVatCents || 0;
    acc.net += s.docnowNetCents || 0;
    return acc;
  }, { billable: 0, revenue: 0, doctorShare: 0, commission: 0, vat: 0, net: 0 });

  const handleBulkGenerate = async () => {
    if (toGenerate.length === 0) {
      toast('Δεν υπάρχουν εκκαθαρίσεις προς δημιουργία.');
      return;
    }
    const label = overwrite
      ? `Δημιουργία/επαναδημιουργία ${toGenerate.length} εκκαθαρίσεων;`
      : `Δημιουργία ${toGenerate.length} εκκαθαρίσεων που λείπουν;`;
    if (!window.confirm(label)) return;

    setBulkProgress({ running: true, done: 0, total: toGenerate.length, errors: [] });
    // Initialize all targeted rows to 'idle' (will switch to 'running' as we hit each).
    setRowStates(prev => {
      const next = { ...prev };
      for (const r of toGenerate) next[r.doctor.id] = 'idle';
      return next;
    });
    const errors = [];

    for (let i = 0; i < toGenerate.length; i++) {
      const r = toGenerate[i];
      // Mark this row as running BEFORE the API call so spinner appears.
      setRowStates(prev => ({ ...prev, [r.doctor.id]: 'running' }));
      try {
        await billingAPI.generateClearing(r.doctor.id, year, month);
        setRowStates(prev => ({ ...prev, [r.doctor.id]: 'success' }));
      } catch (err) {
        const msg = err?.response?.data?.error || err.message || 'unknown error';
        errors.push({ doctorName: r.doctor.name, msg });
        setRowStates(prev => ({ ...prev, [r.doctor.id]: 'error' }));
      }
      setBulkProgress(prev => ({ ...prev, done: i + 1, errors }));
    }

    setBulkProgress(prev => ({ ...prev, running: false }));
    if (errors.length === 0) {
      toast.success(`Δημιουργήθηκαν ${toGenerate.length} εκκαθαρίσεις.`);
    } else {
      toast.error(`Δημιουργήθηκαν ${toGenerate.length - errors.length}/${toGenerate.length}. ${errors.length} σφάλματα.`);
    }
    await loadPreview();
  };

  return (
    <div style={{ padding: '20px 24px' }}>
      <div style={{ marginBottom: 24 }}>
        <h1 style={{ margin: '0 0 6px 0', fontSize: 24, color: 'var(--navy, #0F1B3C)' }}>
          🛠️ Νέα Εκκαθάριση — Προεπισκόπηση
        </h1>
        <div style={{ color: 'var(--muted, #6B7280)', fontSize: 14 }}>
          Δείτε τι θα δημιουργηθεί πριν την μαζική παραγωγή. Δεν γίνεται καμία εγγραφή μέχρι να πατήσετε «Δημιουργία».
        </div>
      </div>

      {/* Period picker */}
      <Card title="Επιλογή Περιόδου">
        <div style={{ display: 'flex', gap: 16, alignItems: 'center', flexWrap: 'wrap' }}>
          <label style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
            <span style={{ fontSize: 12, fontWeight: 600, color: 'var(--muted)' }}>Μήνας</span>
            <select
              value={month}
              onChange={e => setMonth(Number(e.target.value))}
              style={{ padding: '8px 12px', border: '1px solid #D1D5DB', borderRadius: 6, fontSize: 14 }}
            >
              {MONTHS_EL.map((m, i) => (
                <option key={i+1} value={i+1}>{m}</option>
              ))}
            </select>
          </label>
          <label style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
            <span style={{ fontSize: 12, fontWeight: 600, color: 'var(--muted)' }}>Έτος</span>
            <select
              value={year}
              onChange={e => setYear(Number(e.target.value))}
              style={{ padding: '8px 12px', border: '1px solid #D1D5DB', borderRadius: 6, fontSize: 14 }}
            >
              {[2025, 2026, 2027].map(y => <option key={y} value={y}>{y}</option>)}
            </select>
          </label>
          <label style={{ display: 'flex', alignItems: 'center', gap: 8, marginTop: 18 }}>
            <input
              type="checkbox"
              checked={overwrite}
              onChange={e => setOverwrite(e.target.checked)}
            />
            <span style={{ fontSize: 13, color: 'var(--muted)' }}>
              Επαναδημιουργία υπαρχόντων (overwrite)
            </span>
          </label>
        </div>
      </Card>

      {/* Summary card */}
      <div style={{ marginTop: 16 }}>
        <Card title={`Σύνοψη · ${MONTHS_EL[month-1]} ${year}`}>
          {loading ? (
            <div style={{ padding: 24, color: 'var(--muted)' }}>Φόρτωση…</div>
          ) : !preview ? (
            <div style={{ padding: 24, color: 'var(--muted)' }}>—</div>
          ) : (
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(160px, 1fr))', gap: 12 }}>
              <SumStat label="Ενεργοί ιατροί"          value={totalDoctors} />
              <SumStat label="Ήδη υπάρχουν"            value={existingCount} />
              <SumStat label="Λείπουν"                 value={missingCount} highlight={missingCount > 0} />
              <SumStat label="Θα δημιουργηθούν τώρα"   value={toGenerate.length} highlight />
              <SumStat label="Συνολική χρέωση"         value={fmtEuro(totals.revenue)} />
              <SumStat label="Σύνολο σε ιατρούς"       value={fmtEuro(totals.doctorShare)} />
              <SumStat label="Προμήθεια DocNow"        value={fmtEuro(totals.commission)} />
              <SumStat label="ΦΠΑ DocNow"              value={fmtEuro(totals.vat)} />
              <SumStat label="Καθαρό DocNow"           value={fmtEuro(totals.net)} bold />
            </div>
          )}
        </Card>
      </div>

      {/* Action button */}
      <div style={{ marginTop: 16, display: 'flex', gap: 12, alignItems: 'center', flexWrap: 'wrap' }}>
        <button
          onClick={handleBulkGenerate}
          disabled={loading || bulkProgress.running || toGenerate.length === 0}
          style={{
            background: bulkProgress.running || toGenerate.length === 0
              ? '#D1D5DB'
              : 'var(--crimson, #C0185A)',
            color: 'white', border: 'none', borderRadius: 8,
            padding: '12px 20px', fontWeight: 600, fontSize: 14,
            cursor: bulkProgress.running || toGenerate.length === 0 ? 'not-allowed' : 'pointer',
          }}
        >
          🛠️ Δημιουργία {toGenerate.length} εκκαθαρίσεων
        </button>
        {bulkProgress.running && (
          <span style={{ color: 'var(--muted)', fontSize: 13 }}>
            Σε εξέλιξη… {bulkProgress.done}/{bulkProgress.total}
          </span>
        )}
        {!bulkProgress.running && bulkProgress.errors.length > 0 && (
          <span style={{ color: '#991B1B', fontSize: 13 }}>
            {bulkProgress.errors.length} σφάλματα — δείτε λεπτομέρειες στους πίνακες.
          </span>
        )}
      </div>

      {/* Per-doctor table */}
      <div style={{ marginTop: 16 }}>
        <Card title="Ανά Ιατρό">
          {loading ? (
            <div style={{ padding: 24, color: 'var(--muted)' }}>Φόρτωση…</div>
          ) : rows.length === 0 ? (
            <div style={{ padding: 24, color: 'var(--muted)' }}>Δεν βρέθηκαν ενεργοί ιατροί.</div>
          ) : (
            <div style={{ overflowX: 'auto' }}>
              <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
                <thead>
                  <tr style={{ background: '#F9FAFB', borderBottom: '2px solid var(--border, #E5E7EB)' }}>
                    <th style={{ padding: 10, textAlign: 'left' }}>Ιατρός</th>
                    <th style={{ padding: 10, textAlign: 'right' }}>Billable</th>
                    <th style={{ padding: 10, textAlign: 'right' }}>Non-billable</th>
                    <th style={{ padding: 10, textAlign: 'right' }}>Έσοδα</th>
                    <th style={{ padding: 10, textAlign: 'right' }}>Σε ιατρό</th>
                    <th style={{ padding: 10, textAlign: 'right' }}>Προμήθεια DocNow</th>
                    <th style={{ padding: 10, textAlign: 'right' }}>ΦΠΑ</th>
                    <th style={{ padding: 10, textAlign: 'right' }}>Καθαρό DocNow</th>
                    <th style={{ padding: 10, textAlign: 'center' }}>Κατάσταση</th>
                  </tr>
                </thead>
                <tbody>
                  {rows.map(r => {
                    const grayed = !!r.existing && !overwrite;
                    const errored = !!r.error;
                    const s = r.summary;
                    const rowStyle = {
                      borderBottom: '1px solid var(--border, #E5E7EB)',
                      opacity: grayed ? 0.5 : 1,
                      background: errored ? '#FEF2F2' : undefined,
                    };
                    return (
                      <tr key={r.doctor.id} style={rowStyle}>
                        <td style={{ padding: 10 }}>{r.doctor.name}</td>
                        {errored ? (
                          <td colSpan={7} style={{ padding: 10, color: '#991B1B', fontSize: 12 }}>
                            Σφάλμα υπολογισμού: {r.error}
                          </td>
                        ) : (
                          <>
                            <td style={{ padding: 10, textAlign: 'right' }}>{s?.billableCount ?? '—'}</td>
                            <td style={{ padding: 10, textAlign: 'right' }}>{s?.nonBillableCount ?? '—'}</td>
                            <td style={{ padding: 10, textAlign: 'right' }}>{fmtEuro(s?.totalRevenueCents)}</td>
                            <td style={{ padding: 10, textAlign: 'right' }}>{fmtEuro(s?.doctorShareCents)}</td>
                            <td style={{ padding: 10, textAlign: 'right' }}>{fmtEuro(s?.docnowCommissionCents)}</td>
                            <td style={{ padding: 10, textAlign: 'right' }}>{fmtEuro(s?.docnowVatCents)}</td>
                            <td style={{ padding: 10, textAlign: 'right', fontWeight: 600 }}>{fmtEuro(s?.docnowNetCents)}</td>
                          </>
                        )}
                        <td style={{ padding: 10, textAlign: 'center' }}>
                          {/* @d4-stage3-per-row-state: live row state takes priority during bulk-generate */}
                          {rowStates[r.doctor.id] === 'running' ? (
                            <span style={{ fontSize: 11, color: '#6B7280', fontWeight: 600 }}>
                              ⏳ Δημιουργία…
                            </span>
                          ) : rowStates[r.doctor.id] === 'success' ? (
                            <span style={{
                              padding: '4px 10px', borderRadius: 12,
                              fontSize: 11, fontWeight: 600,
                              background: '#D1FAE5', color: '#065F46',
                            }}>
                              ✓ Δημιουργήθηκε
                            </span>
                          ) : rowStates[r.doctor.id] === 'error' ? (
                            <span style={{
                              padding: '4px 10px', borderRadius: 12,
                              fontSize: 11, fontWeight: 600,
                              background: '#FEE2E2', color: '#991B1B',
                            }}>
                              ✗ Σφάλμα
                            </span>
                          ) : r.existing ? (
                            <ExistingBadge status={r.existing.payoutStatus} />
                          ) : (
                            <span style={{ fontSize: 11, color: '#10B981', fontWeight: 600 }}>
                              θα δημιουργηθεί
                            </span>
                          )}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </Card>
      </div>

      {/* Errors detail */}
      {bulkProgress.errors.length > 0 && !bulkProgress.running && (
        <div style={{ marginTop: 16 }}>
          <Card title={`Σφάλματα (${bulkProgress.errors.length})`}>
            <ul style={{ margin: 0, paddingLeft: 20, color: '#991B1B', fontSize: 13 }}>
              {bulkProgress.errors.map((e, i) => (
                <li key={i} style={{ marginBottom: 4 }}>
                  <strong>{e.doctorName}:</strong> {e.msg}
                </li>
              ))}
            </ul>
          </Card>
        </div>
      )}
    </div>
  );
}

function SumStat({ label, value, highlight, bold }) {
  return (
    <div style={{
      padding: '10px 12px',
      borderRadius: 8,
      background: highlight ? '#FEF3C7' : '#F9FAFB',
      border: highlight ? '1px solid #F59E0B' : '1px solid var(--border, #E5E7EB)',
    }}>
      <div style={{ fontSize: 11, color: 'var(--muted, #6B7280)', marginBottom: 4 }}>{label}</div>
      <div style={{ fontSize: bold ? 18 : 16, fontWeight: bold ? 700 : 600, color: 'var(--navy, #0F1B3C)' }}>
        {value}
      </div>
    </div>
  );
}
